import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import IcomoonReact from 'icomoon-react';
import PropTypes from 'prop-types';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { CellMeasurerCache } from 'react-virtualized';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import DraggableContentWrapper from './DraggableContentWrapper';
import { setIsBeforeRecurringSectionExpanded } from '../../ducks/calendarActions';

const RecurringHeader = styled('div')`
  flex-direction: column;
  display: flex;
  background-color: #F8F8F8;
  border-radius: 4px;
  margin-bottom: 9px;
  margin-top: ${(props) => props.addPaddingTop && '9px'};
  min-height: 48px;
`;

const IconContainer = styled('div')`
  padding-left: ${(props) => (props.viewType === 'weekView' ? '10px' : '28px')};
  cursor: pointer;
  display: flex;
  padding-bottom: 8px;
  padding-top: 8px;
  background-color: #F8F8F8;
  border-radius: 4px;
`;

const RecurringText = styled('div')`
  margin-left: 7px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const IconContainerWrapper = styled('div')`
  position: relative;
  background: #444444;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #444444;
  min-height: 25px;
  min-width: 25px;
  max-width: 25px;
  max-height: 25px;
`;

const BeforeRecurringWorkouts = styled('div')`
  display: flex;
  flex-direction: column;
  transition: max-height 0.3s ease;
  max-width: 100%;
  overflow: hidden;
  background-color: #F8F8F8;
  border-radius: 4px;
`;

const LineContainer = styled('div')`
  min-height: 32px;
  position: relative;
  flex-grow: 1;
  margin-left: 10px;
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 2px;
    background-color: #444444;
  }
  width: 100%;
  overflow: hidden;
`;

const ExpandCollapseContainer = styled('div')`
  :hover {
    cursor: pointer;
    background: rgba(119, 167, 255, 0.2);
  }
  height: 32px;
  width: 32px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 10px;

  .expandCollapseIcon {
    fill: lightgray;
  }

  &:hover .expandCollapseIcon {
    fill: #77A7FF;
  }
`;

const BeforeRecurringVirtualRow = React.memo(({
  measure,
  cache,
  date,
  dayKey,
  onDayExpand,
  isDayExpanded,
}) => {
  const dispatch = useDispatch();
  const beforeTooltipId = 'before-expand-tooltip';
  const formatDate = (d) => `${moment(d).format('YYYY-MM-DD')}`;
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const effectiveDate = date || selectedDate;
  const beforeRecurringWorkouts = useSelector(
    (state) => state.calendar.data.beforeRecurringWorkouts,
  );
  const calendarItems = useSelector((state) => state.calendar.data.calendarItems[moment(effectiveDate).format('YYYY-MM-DD')]?.exercises || []);
  const isBeforeExpandedDayView = useSelector(
    (state) => state.calendar.data.isBeforeRecurringSectionExpanded,
  );
  const isBeforeExpandedWeekView = isBeforeExpandedDayView[dayKey];
  const isBeforeExpanded = viewType === 'weekView' ? isBeforeExpandedWeekView : isBeforeExpandedDayView;

  const weekViewHorizontallyExpandedDay = useSelector(
    (state) => state.calendar.data.weekViewExpandedDay,
  );
  const isRecurringRowInHorizontallyExpandedDay = dayKey === weekViewHorizontallyExpandedDay;

  return (
    <RecurringHeader
      addPaddingTop={viewType === 'dayView' && !calendarItems?.length}
    >
      <IconContainer
        onClick={() => {
          if (viewType === 'weekView') {
            dispatch(setIsBeforeRecurringSectionExpanded({
              ...isBeforeExpanded,
              [dayKey]: !isBeforeExpanded,
            }));
            if (!isDayExpanded && onDayExpand) {
              onDayExpand();
            }
          } else {
            dispatch(setIsBeforeRecurringSectionExpanded(!isBeforeExpanded));
          }
        }}
        viewType={viewType}
      >
        <IconContainerWrapper>
          <IcomoonReact
            iconSet={iconSet}
            size={15}
            icon='recurring-circle'
            color='white'
          />
        </IconContainerWrapper>
        <LineContainer>
          <RecurringText>
            Recurring (
            {beforeRecurringWorkouts[formatDate(effectiveDate)].length}
            )
          </RecurringText>
        </LineContainer>
        {(viewType === 'dayView' || (viewType === 'weekView' && isRecurringRowInHorizontallyExpandedDay)) && (
        <ExpandCollapseContainer
          onClick={() => {
            if (viewType === 'weekView') {
              dispatch(setIsBeforeRecurringSectionExpanded({
                ...isBeforeExpanded,
                [dayKey]: !isBeforeExpanded,
              }));
            } else {
              dispatch(setIsBeforeRecurringSectionExpanded(!isBeforeExpanded));
            }
          }}
          data-tooltip-id={beforeTooltipId}
        >
          <IcomoonReact
            iconSet={iconSet}
            size={15}
            icon={isBeforeExpanded ? 'collapse' : 'expand2'}
            className='expandCollapseIcon'
          />
          <ReactTooltip
            id={beforeTooltipId}
            place='left'
            content={isBeforeExpanded ? 'Collapse' : 'Expand'}
            style={{
              backgroundColor: 'rgba(68, 68, 68, 1)',
              color: 'white',
              fontFamily: 'Nunito Sans',
              fontSize: '13px',
              zIndex: 1000,
            }}
          />
        </ExpandCollapseContainer>
        )}
      </IconContainer>
      <BeforeRecurringWorkouts>
        <div>
          {beforeRecurringWorkouts[formatDate(effectiveDate)]
            ? beforeRecurringWorkouts[formatDate(effectiveDate)].map((recurringWorkout, idx) => (
              <div key={recurringWorkout.assignedId || idx}>
                <DraggableContentWrapper
                  recurringWorkout={recurringWorkout}
                  idx={idx}
                  beforeOrAfter='before'
                  measure={measure}
                  isBeforeExpanded={isBeforeExpanded}
                  cache={cache}
                  date={date}
                  onDayExpand={onDayExpand}
                  isDayExpanded={isDayExpanded}
                />
              </div>
            ))
            : null}
        </div>
      </BeforeRecurringWorkouts>
    </RecurringHeader>
  );
});

BeforeRecurringVirtualRow.defaultProps = {
  measure: () => {},
  cache: {},
  date: null,
  dayKey: null,
  isDayExpanded: false,
  onDayExpand: () => {},
};

BeforeRecurringVirtualRow.propTypes = {
  measure: PropTypes.func,
  cache: PropTypes.instanceOf(CellMeasurerCache),
  date: PropTypes.string,
  dayKey: PropTypes.string,
  isDayExpanded: PropTypes.bool,
  onDayExpand: PropTypes.func,
};

export default BeforeRecurringVirtualRow;
